import { surveysApi } from '../services/api';
import type { Survey as ApiSurvey } from '../services/api';
import type { SurveyQuestion } from '../components/SurveyQuestions';

/**
 * Project-survey loading for StoreDetail (#2088, diag #48).
 *
 * The old in-component logic responded to ANY fetch error by rendering
 * DEMO_SURVEY_QUESTIONS — reps ended up answering fake questions on real
 * projects (diag #48: 5 demo questions on project 10131654). The three
 * outcomes are now explicit, and demo/mock data is not reachable from any
 * production path:
 *
 *  - 'loaded': the project has a survey; transformed questions returned.
 *  - 'none':   the project genuinely has no survey (server returns
 *              success with data: null — a normal state, not an error).
 *  - 'error':  the fetch failed (network/server); callers must surface a
 *              retryable error state, never substitute questions.
 */
export type SurveyLoadResult =
  | { kind: 'loaded'; surveyId: number; questions: SurveyQuestion[] }
  | { kind: 'none' }
  | { kind: 'error' };

/**
 * Parse the /api/surveys/project/<id>/ response shape. The API wraps data
 * in { status, message, data }; older callers also tolerated an unwrapped
 * body. data: null (or a body with no questions) means "no survey".
 * Pure — unit-testable without the network.
 */
export function parseSurveyResponse(response: unknown): SurveyLoadResult {
  const wrapper = response as { data?: ApiSurvey | null } | null | undefined;
  // Distinguish "wrapper with data: null" (no survey) from an unwrapped body.
  const surveyData: ApiSurvey | null | undefined =
    wrapper && typeof wrapper === 'object' && 'data' in wrapper ? wrapper.data : (response as ApiSurvey | null);

  if (!surveyData || !surveyData.questions || surveyData.questions.length === 0) {
    return { kind: 'none' };
  }

  // Transform API field names (lowercase optionid/optionvalue) to the
  // component's camelCase shape.
  const questions: SurveyQuestion[] = surveyData.questions.map((q) => ({
    qid: q.qid,
    question: q.question,
    qtypeid: q.qtypeid,
    mandatory: q.mandatory,
    options: q.options?.map((opt) => ({
      optionId: (opt as { optionid?: number; optionId?: number }).optionid
        ?? (opt as { optionId?: number }).optionId
        ?? 0,
      optionValue: (opt as { optionvalue?: string; optionValue?: string }).optionvalue
        ?? (opt as { optionValue?: string }).optionValue
        ?? '',
    })),
  }));

  return { kind: 'loaded', surveyId: surveyData.surveyid, questions };
}

/** Fetch + parse a project's survey. Never throws; never invents questions. */
export async function loadProjectSurvey(projectId: number): Promise<SurveyLoadResult> {
  try {
    const response = await surveysApi.getSurveyByProject(projectId);
    return parseSurveyResponse(response);
  } catch {
    return { kind: 'error' };
  }
}
